<template>
  <div
    id="product-form"
    class="product-form"
    :class="{ [$class('loading')]: loading || cartContainsEvalProduct }"
    data-sign-post-location="main"
    js-sign-post
  >
    <div
      v-if="cartContainsEvalProduct && showEvalCopyError"
      class="product-form__eval-copy-wrapper"
    >
      <div>
        <p
          class="product-form__eval-copy-title text-body-s-bold"
          v-text="getErrorData.title"
        />
        <p
          class="product-form__eval-copy-description text-body-s-regular"
          v-text="getErrorData.description"
        />
      </div>
      <button
        type="button"
        class="product-form__eval-copy-button"
        :aria-label="$string('accessibility.close')"
        @click="showEvalCopyError = false"
      >
        <icon-close />
      </button>
    </div>

    <form
      class="product-form__form"
      @submit.prevent="handleAddToCart"
    >
      <div
        v-if="isPersonalized"
        class="product-form__custom-message-block"
      >
        <label
          class="text-body-s-bold"
          for="custom_message"
          v-text="$string('product.custom_message.label')"
        />
        <textarea
          id="custom_message"
          ref="personalMessageTextarea"
          class="product-form__custom-message"
          :placeholder="$string('product.custom_message.placeholder')"
          :maxlength="250"
          name="properties[Custom Message]"
          required
          @input="checkPersonalizedMessage"
        />
        <p
          class="text-body-s-regular"
          v-text="$string('product.custom_message.length_info')"
        />
      </div>

      <div class="product-form__details">
        <product-options
          v-show="
            !product.hasOnlyDefaultVariant ||
              section.displaySingleValueOptions && section.displaySingleVariantOption
          "
          class="product-form__options"
          :loading="loading"
          :product="product"
          :section="section"
          :variant="variant"
          @update-variant="handleUpdateVariant"
        />

        <quantity-selector
          v-if="section.displayQuantitySelector &&
            !(overriddenButton && overriddenButton['override-active'])"
          class="product-form__quantity-selector mr-m"
          :min="1"
          :quantity="quantity"
          :is-readonly="false"
          :state="loading ? 'loading' : 'ready'"
          @update-quantity="handleUpdateQuantity"
        />

        <input
          v-else
          name="quantity"
          readonly
          type="hidden"
          :value="quantity"
        >

        <btn
          v-if="overriddenButton && overriddenButton['override-active']"
          :id="overriddenButton.id"
          class="product-form__add-to-cart"
          :class="{ [$class('disabled')]: loading }"
          :label="overriddenButton.text"
          :target="overriddenButton['open-new-window'] ? '_blank' : '_self'"
          :url="overriddenButton.url"
          type="button"
        />

        <btn
          v-else
          class="product-form__add-to-cart"
          :class="{ [$class('disabled')]: isCTADisabled }"
          :label="addToCartText"
          :show-state-icon="state === 'adding'"
          :disabled="!isSubmitEnabled"
          type="submit"
        >
          <template #stateIcon>
            <icon-loading />
          </template>
        </btn>
      </div>
    </form>

    <div
      v-if="overriddenButton && overriddenButton['override-active']"
      class="product-form__info-box mt-xl"
    >
      <icon-info />
      <p
        class="product-form__info-box-text text-body-s-regular"
        v-html="overriddenButton['supporting-text']"
      />
    </div>

    <notification-panel
      class-container="product-form__error"
      :show-close="false"
      :text="error"
      type="error"
    />
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import NotificationPanel from '~async/utils/notification-panel/notification-panel'
import ProductOptions from '~async/product-form/product-options'
import QuantitySelector from '~async/quantity-selector/quantity-selector'

import IconLoading from '~icons/general/loading.svg'
import IconClose from '~icons/directional-navigation/close.svg'
import IconInfo from '~icons/general/info.svg'

import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    IconLoading,
    IconClose,
    NotificationPanel,
    ProductOptions,
    QuantitySelector,
    IconInfo,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    isPersonalized: {
      type: Boolean,
      default: false,
    },
    overriddenButton: {
      type: [Boolean, Object],
      default: () => false,
    },
    section: {
      type: Object,
      default: () => ({
        autoOpenCartDrawer: true,
        displayQuantitySelector: true,
        displaySingleOptionNames: true,
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        excludedInitialSelectOptions: ['length', 'size', 'waist', 'width'],
        id: '',
        initialSelect: true,
        resetFormBehaviour: 'disabled',
        selectDisabledValues: true,
      }),
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    cartItems: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
  },

  data() {
    return {
      error: '',
      lineItemProperties: {},
      quantity: 1,
      state: 'ready',
      timeout: {},
      cartContainsEvalProduct: false,
      showEvalCopyError: true,
      isSubmitEnabled: !this.isPersonalized,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
      getErrorData: 'product-eval-copy/getErrorData',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.variant) {
        return this.$string('product.cta.select_variant')
      }

      if (!this.product.publishedProduct) {
        return this.$string('product.cta.unpublished')
      }

      if (!this.variant.available) {
        return this.$string('product.cta.sold_out')
      }

      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    isCTADisabled() {
      return this.loading ||
        !this.variant ||
        !this.variant.available ||
        !this.product.publishedProduct
    },
  },

  watch: {

    /**
     * Watch product to reset quantity and error.
     */
    product() {
      this.quantity = 1
      this.error = ''
    },

    /**
     * Watch variant to reset error.
     */
    variant() {
      this.error = ''
    },

    cartItems() {
      this.checkEvalProductInCart()
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.product.publishedProduct) {
        this.error = this.$string(
          'product.unpublished_message',
          {
            replace: { product: this.product.title },
          },
        )

        return
      }

      if (!this.variant) {
        this.error = this.$string('product.form.select_variant')
        return
      }

      if (!this.variant.available) {
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (cnvs.storybook) {
        if (!this.variant.available) {
          this.error = this.$string('product.form.sold_out')
          this.state = 'ready'
          return
        }

        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, this.$timing('slow'))

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

        return
      }

      if (this.$refs.personalMessageTextarea) {
        const trimmedCustomMessage = this.$refs.personalMessageTextarea.value.trim()

        if (trimmedCustomMessage) {
          this.lineItemProperties['Custom Message'] = trimmedCustomMessage
        }
      }

      const items = [
        {
          id: this.variant.id,
          properties: this.lineItemProperties,
          quantity: this.quantity,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'added'

        if (
          this.section.autoOpenCartDrawer &&
          this.getComponentExists('cart-drawer')
        ) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        await this.resetState()

      } catch (error) {
        this.state = 'error'

        if (error?.description) {
          this.error = error.description
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'))
      })

      this.state = 'ready'

      /**
       * Clear variant selection based on setting.
       */
      if (this.section.resetFormBehaviour !== 'clear') {
        return
      }

      this.handleUpdateVariant(false)
      this.quantity = 1
    },

    /**
     * Handle update variant event.
     * @param {Object} variant - Emitted variant object.
     */
    handleUpdateVariant(variant) {
      this.$emit('update-variant', variant)
    },

    /**
     * Handle update quantity event.
     * @param {String} quantity - Emitted quantity.
     */
    handleUpdateQuantity(quantity) {
      this.quantity = quantity
    },

    /**
     * Checks if there is regular product in basket
     */
    checkEvalProductInCart() {
      this.cartContainsEvalProduct = this.cartItems.some((item) => {
        return item.properties?.[this.$variable('constants.LINE_ITEM_PROPERTY_EVALUATION')]
      })
    },

    /**
     * Hides evaluation product error
     */

    closeEvalProductError() {
      this.showEvalCopyError = false
    },

    /*
     * Checks character count inside custom message box.
     */
    checkPersonalizedMessage() {
      this.isSubmitEnabled = this.$refs.personalMessageTextarea.value.trim().length > 0
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
</style>
